import {
  UzButton,
  UzCard,
  UzDatePicker,
  UzDialog,
  UzInput,
  UzMoneyInput,
  UzSelect,
} from '@utazzo/ui';
import { CalendarClock, ChevronDown, ChevronRight, PlusCircle, Trash2 } from 'lucide-react';
import React, { useEffect, useState } from 'react';

import { formatDateDMY, formatInrPaise, SettlementBadge } from '../invoices.helpers';
import {
  useBankAccountOptions,
  useInvoiceSupplierLedger,
  useRecordSupplierPayment,
  useSaveSupplierPaymentPlan,
  useVoidSupplierPayment,
} from '../use-invoices';

import { rupeesToPaise, toIsoDate } from './invoice-form.helpers';

import type {
  RecordSupplierPaymentRequest,
  SaveSupplierPaymentPlanRequest,
  SupplierLine,
  SupplierPaymentPlanTerm,
  SupplierPaymentRow,
} from '@utazzo/shared';

/**
 * Admin panel (screen-only, never printed) for the supplier payable lines of an
 * invoice: each line's payable / settled / outstanding, a settlement-status
 * pill, a record-payment action and the expandable payment history. Mirrors the
 * InvoiceAdminPanel styling.
 */

export const SupplierSettlementPanel = ({
  invoiceId,
}: {
  invoiceId: string;
}): React.JSX.Element => {
  const ledgerQuery = useInvoiceSupplierLedger(invoiceId);
  const lines = ledgerQuery.data ?? [];

  return (
    <UzCard
      className="invoice-no-print mx-auto max-w-3xl"
      title="Supplier settlement"
      description="Buying cost per supplier and what has been paid against it."
    >
      {ledgerQuery.isLoading ? (
        <p className="text-sm text-zinc-500">Loading supplier lines...</p>
      ) : lines.length === 0 ? (
        <p className="text-sm text-zinc-500">No supplier lines on this invoice.</p>
      ) : (
        <div className="space-y-3">
          {lines.map((line) => (
            <SupplierLineCard key={line.id} invoiceId={invoiceId} line={line} />
          ))}
        </div>
      )}
    </UzCard>
  );
};

const SupplierLineCard = ({
  invoiceId,
  line,
}: {
  invoiceId: string;
  line: SupplierLine;
}): React.JSX.Element => {
  const [dialogOpen, setDialogOpen] = useState(false);
  const [planOpen, setPlanOpen] = useState(false);
  // The supplier payment being edited (void + re-record), or null for a new one.
  const [editing, setEditing] = useState<SupplierPaymentRow | null>(null);
  const voidPayment = useVoidSupplierPayment(invoiceId);
  const [expanded, setExpanded] = useState(false);
  const payments = line.payments ?? [];
  // The list shows only current (non-voided) payments; superseded ones are kept
  // for audit under a separate history disclosure.
  const currentPayments = payments.filter((p) => !p.voidedAt);
  const voidedPayments = payments.filter((p) => !!p.voidedAt);
  const plan = line.paymentPlan ?? [];

  const openRecord = (): void => {
    setEditing(null);
    setDialogOpen(true);
  };
  const openEdit = (p: SupplierPaymentRow): void => {
    setEditing(p);
    setDialogOpen(true);
  };

  return (
    <div className="rounded-md border border-zinc-100 p-3">
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div className="min-w-0 space-y-1">
          <div className="flex items-center gap-2">
            <span className="text-sm font-semibold text-zinc-900">
              {line.supplierName ?? 'Supplier'}
            </span>
            <SettlementBadge status={line.settlementStatus} />
          </div>
          {line.description && <div className="text-sm text-zinc-600">{line.description}</div>}
          {line.paymentDueDate && (
            <div className="text-xs text-zinc-500">Due {formatDateDMY(line.paymentDueDate)}</div>
          )}
        </div>

        <div className="flex flex-wrap items-center gap-6">
          <Metric label="Payable" value={formatInrPaise(line.payablePaise)} />
          <Metric label="Settled" value={formatInrPaise(line.amountSettledPaise)} />
          <Metric
            label="Outstanding"
            value={formatInrPaise(line.outstandingPaise)}
            highlight={Number(line.outstandingPaise) > 0}
          />
        </div>
      </div>

      {plan.length > 0 && (
        <div className="mt-3 border-t border-zinc-100 pt-3">
          <div className="mb-2 flex items-center justify-between">
            <span className="text-xs font-medium text-zinc-500">
              Payment plan {'·'} {plan.length} term{plan.length === 1 ? '' : 's'} of{' '}
              {formatInrPaise(line.plannedTotalPaise)}
            </span>
            {Number(line.plannedTotalPaise) !== Number(line.payablePaise) && (
              <span className="text-xs font-medium text-amber-600">
                {formatInrPaise(line.plannedTotalPaise)} planned vs{' '}
                {formatInrPaise(line.payablePaise)} payable
              </span>
            )}
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-zinc-200 text-left text-xs font-medium text-zinc-500">
                  <th className="py-1.5 pr-4">Term</th>
                  <th className="py-1.5 pr-4">Due</th>
                  <th className="py-1.5 pr-4 text-right">Amount</th>
                  <th className="py-1.5 pr-4 text-right">Paid</th>
                  <th className="py-1.5">Status</th>
                </tr>
              </thead>
              <tbody>
                {plan.map((term) => (
                  <tr key={term.id} className="border-b border-zinc-100 last:border-0">
                    <td className="py-1.5 pr-4 tabular-nums">#{term.termNo}</td>
                    <td className="py-1.5 pr-4 whitespace-nowrap">
                      {term.dueDate ? formatDateDMY(term.dueDate) : '-'}
                    </td>
                    <td className="py-1.5 pr-4 text-right whitespace-nowrap tabular-nums">
                      {formatInrPaise(term.amountPaise)}
                    </td>
                    <td className="py-1.5 pr-4 text-right whitespace-nowrap tabular-nums">
                      {formatInrPaise(term.paidPaise)}
                    </td>
                    <td className="py-1.5">
                      <PlanTermBadge status={term.status} />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      <div className="mt-3 flex items-center justify-between">
        {payments.length > 0 ? (
          <button
            type="button"
            onClick={() => setExpanded((v) => !v)}
            className="inline-flex items-center gap-1 text-xs font-medium text-zinc-600 hover:text-zinc-900"
          >
            {expanded ? (
              <ChevronDown className="h-3.5 w-3.5" />
            ) : (
              <ChevronRight className="h-3.5 w-3.5" />
            )}
            {currentPayments.length} payment{currentPayments.length === 1 ? '' : 's'}
            {voidedPayments.length > 0 && (
              <span className="text-zinc-400"> (+{voidedPayments.length} superseded)</span>
            )}
          </button>
        ) : (
          <span className="text-xs text-zinc-400">No payments yet</span>
        )}
        <div className="flex items-center gap-2">
          <UzButton
            size="sm"
            variant="ghost"
            leftIcon={<CalendarClock className="h-4 w-4" />}
            onClick={() => setPlanOpen(true)}
          >
            {plan.length > 0 ? 'Edit plan' : 'Plan installments'}
          </UzButton>
          {line.settlementStatus !== 'PAID' && (
            <UzButton
              size="sm"
              variant="outline"
              leftIcon={<PlusCircle className="h-4 w-4" />}
              onClick={openRecord}
            >
              Record payment
            </UzButton>
          )}
        </div>
      </div>

      {expanded && payments.length > 0 && (
        <div className="mt-3 space-y-4 border-t border-zinc-100 pt-3">
          {currentPayments.length > 0 && (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-zinc-200 text-left text-xs font-medium text-zinc-500">
                    <th className="py-2 pr-4">Date</th>
                    <th className="py-2 pr-4 text-right">Amount</th>
                    <th className="py-2 pr-4 text-right">TDS</th>
                    <th className="py-2 pr-4">Bank</th>
                    <th className="py-2 pr-4">Reference</th>
                    <th className="py-2 text-right" />
                  </tr>
                </thead>
                <tbody>
                  {currentPayments.map((p) => (
                    <tr key={p.id} className="border-b border-zinc-100 last:border-0">
                      <td className="py-2 pr-4 whitespace-nowrap tabular-nums">
                        {formatDateDMY(p.paymentDate)}
                      </td>
                      <td className="py-2 pr-4 text-right whitespace-nowrap tabular-nums">
                        {formatInrPaise(p.amountPaise)}
                      </td>
                      <td className="py-2 pr-4 text-right whitespace-nowrap tabular-nums">
                        {formatInrPaise(p.tdsPaise)}
                      </td>
                      <td className="py-2 pr-4">{p.bankName ?? p.mode ?? '-'}</td>
                      <td className="py-2 pr-4">{p.reference ?? '-'}</td>
                      <td className="py-2 text-right">
                        <div className="flex justify-end gap-3">
                          <button
                            type="button"
                            className="text-xs font-medium text-zinc-600 hover:underline"
                            onClick={() => openEdit(p)}
                          >
                            Edit
                          </button>
                          <button
                            type="button"
                            className="text-xs font-medium text-red-600 hover:underline disabled:opacity-50"
                            disabled={voidPayment.isPending}
                            onClick={() => {
                              if (
                                window.confirm(
                                  'Void this payment? It will be excluded from settled.',
                                )
                              )
                                voidPayment.mutate(p.id);
                            }}
                          >
                            Void
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          {voidedPayments.length > 0 && (
            <div>
              <p className="mb-1 text-[11px] font-semibold tracking-wider text-zinc-400 uppercase">
                Superseded (history)
              </p>
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <tbody>
                    {voidedPayments.map((p) => (
                      <tr
                        key={p.id}
                        className="border-b border-zinc-100 text-zinc-400 last:border-0"
                      >
                        <td className="py-1.5 pr-4 whitespace-nowrap tabular-nums line-through">
                          {formatDateDMY(p.paymentDate)}
                        </td>
                        <td className="py-1.5 pr-4 text-right tabular-nums line-through">
                          {formatInrPaise(p.amountPaise)}
                        </td>
                        <td className="py-1.5 pr-4">{p.bankName ?? p.mode ?? '-'}</td>
                        <td className="py-1.5 pr-4">{p.reference ?? '-'}</td>
                        <td className="py-1.5 text-xs whitespace-nowrap">
                          Voided {p.voidedAt ? formatDateDMY(p.voidedAt) : ''}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </div>
      )}

      <RecordSupplierPaymentDialog
        invoiceId={invoiceId}
        line={line}
        open={dialogOpen}
        onOpenChange={(o) => {
          setDialogOpen(o);
          if (!o) setEditing(null);
        }}
        editing={editing}
      />
      <PlanInstallmentsDialog
        invoiceId={invoiceId}
        line={line}
        open={planOpen}
        onOpenChange={setPlanOpen}
      />
    </div>
  );
};

const PLAN_BADGE: Record<SupplierPaymentPlanTerm['status'], { label: string; cls: string }> = {
  PAID: { label: 'Paid', cls: 'bg-emerald-50 text-emerald-700' },
  PARTIAL: { label: 'Partial', cls: 'bg-amber-50 text-amber-700' },
  PENDING: { label: 'Pending', cls: 'bg-zinc-100 text-zinc-600' },
};

const PlanTermBadge = ({
  status,
}: {
  status: SupplierPaymentPlanTerm['status'];
}): React.JSX.Element => {
  const { label, cls } = PLAN_BADGE[status];
  return (
    <span className={`inline-flex rounded px-1.5 py-0.5 text-xs font-medium ${cls}`}>{label}</span>
  );
};

const Metric = ({
  label,
  value,
  highlight,
}: {
  label: string;
  value: string;
  highlight?: boolean;
}): React.JSX.Element => (
  <div className="text-right">
    <div className="text-xs font-medium text-zinc-500">{label}</div>
    <div
      className={
        highlight
          ? 'text-sm font-semibold text-red-600 tabular-nums'
          : 'text-sm font-semibold text-zinc-900 tabular-nums'
      }
    >
      {value}
    </div>
  </div>
);

const RecordSupplierPaymentDialog = ({
  invoiceId,
  line,
  open,
  onOpenChange,
  editing,
}: {
  invoiceId: string;
  line: SupplierLine;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  editing: SupplierPaymentRow | null;
}): React.JSX.Element => {
  const record = useRecordSupplierPayment(invoiceId);
  const voidPayment = useVoidSupplierPayment(invoiceId);
  const bankOptions = useBankAccountOptions();
  const isEdit = !!editing;

  const outstandingRupees = Number(line.outstandingPaise) / 100;
  const [amount, setAmount] = useState<number | ''>(outstandingRupees > 0 ? outstandingRupees : '');
  const [tds, setTds] = useState<number | ''>('');
  const [paymentDate, setPaymentDate] = useState<Date | null>(new Date());
  const [mode, setMode] = useState('');
  const [reference, setReference] = useState('');
  const [bankAccountId, setBankAccountId] = useState('');

  useEffect(() => {
    if (!open) return;
    if (editing) {
      setAmount(Number(editing.amountPaise) / 100);
      setTds(Number(editing.tdsPaise) > 0 ? Number(editing.tdsPaise) / 100 : '');
      setPaymentDate(editing.paymentDate ? new Date(editing.paymentDate) : new Date());
      setMode(editing.mode ?? '');
      setReference(editing.reference ?? '');
      setBankAccountId(editing.bankAccountId ?? '');
    } else {
      setAmount(outstandingRupees > 0 ? outstandingRupees : '');
      setTds('');
      setPaymentDate(new Date());
      setMode('');
      setReference('');
      setBankAccountId('');
    }
  }, [open, outstandingRupees, editing]);

  const busy = record.isPending || voidPayment.isPending;
  const amountValid = typeof amount === 'number' && amount > 0;
  const canSubmit = amountValid && !!paymentDate && !!bankAccountId && !busy;

  const handleSubmit = (): void => {
    if (!amountValid || !paymentDate || !bankAccountId) return;
    const body: RecordSupplierPaymentRequest = {
      amountPaise: rupeesToPaise(amount),
      paymentDate: toIsoDate(paymentDate) as string,
      bankAccountId,
      ...(typeof tds === 'number' && tds > 0 ? { tdsPaise: rupeesToPaise(tds) } : {}),
      ...(mode.trim() ? { mode: mode.trim() } : {}),
      ...(reference.trim() ? { reference: reference.trim() } : {}),
    };
    // Edit = void the old payment, then record the corrected one.
    const doRecord = (): void => {
      record.mutate({ invoiceSupplierId: line.id, body }, { onSuccess: () => onOpenChange(false) });
    };
    if (editing) {
      voidPayment.mutate(editing.id, { onSuccess: doRecord });
    } else {
      doRecord();
    }
  };

  return (
    <UzDialogWrapper
      open={open}
      onOpenChange={onOpenChange}
      supplierName={line.supplierName}
      canSubmit={canSubmit}
      pending={busy}
      onSubmit={handleSubmit}
      isEdit={isEdit}
    >
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
        <UzMoneyInput
          label="Amount"
          required
          value={amount}
          onValueChange={(v) => setAmount(v ?? '')}
        />
        <UzMoneyInput label="TDS withheld" value={tds} onValueChange={(v) => setTds(v ?? '')} />
        <UzDatePicker
          label="Payment date"
          required
          value={paymentDate}
          onValueChange={setPaymentDate}
        />
        <UzSelect
          label="Paid from bank"
          required
          placeholder="Select bank account"
          value={bankAccountId}
          onValueChange={setBankAccountId}
          options={(bankOptions.data ?? []).map((b) => ({
            value: b.id,
            label: `${b.label} - ${b.bankName} (${b.accountNumber})`,
          }))}
        />
        <UzInput label="Mode" value={mode} onChange={(e) => setMode(e.target.value)} />
        <UzInput
          label="Reference"
          value={reference}
          onChange={(e) => setReference(e.target.value)}
          containerClassName="sm:col-span-2"
        />
      </div>
    </UzDialogWrapper>
  );
};

type PlanRowDraft = { amount: number | ''; dueDate: Date | null; note: string };

/**
 * Editor for a supplier line's installment PLAN: N terms of amount + optional
 * due date + note. Saving replaces the whole plan; clearing all rows removes it.
 * The plan is working-capital scheduling only - it never touches settlement.
 */
const PlanInstallmentsDialog = ({
  invoiceId,
  line,
  open,
  onOpenChange,
}: {
  invoiceId: string;
  line: SupplierLine;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}): React.JSX.Element => {
  const save = useSaveSupplierPaymentPlan(invoiceId);
  const [rows, setRows] = useState<PlanRowDraft[]>([]);

  useEffect(() => {
    if (!open) return;
    const existing = line.paymentPlan ?? [];
    setRows(
      existing.length > 0
        ? existing.map((t) => ({
            amount: Number(t.amountPaise) / 100,
            dueDate: t.dueDate ? new Date(t.dueDate) : null,
            note: t.note ?? '',
          }))
        : [{ amount: '', dueDate: null, note: '' }],
    );
  }, [open, line.paymentPlan]);

  const payableRupees = Number(line.payablePaise) / 100;
  const plannedRupees = rows.reduce((s, r) => s + (typeof r.amount === 'number' ? r.amount : 0), 0);
  const filledRows = rows.filter((r) => typeof r.amount === 'number' && r.amount > 0);
  const diff = plannedRupees - payableRupees;

  const setRow = (i: number, patch: Partial<PlanRowDraft>): void =>
    setRows((prev) => prev.map((r, idx) => (idx === i ? { ...r, ...patch } : r)));
  const addRow = (): void => setRows((prev) => [...prev, { amount: '', dueDate: null, note: '' }]);
  const removeRow = (i: number): void => setRows((prev) => prev.filter((_, idx) => idx !== i));

  /** Split the payable evenly across the current number of rows (rounded, remainder on last). */
  const splitEvenly = (): void => {
    const n = rows.length || 1;
    const per = Math.floor((payableRupees / n) * 100) / 100;
    setRows((prev) =>
      prev.map((r, idx) => ({
        ...r,
        amount:
          idx === prev.length - 1 ? Math.round((payableRupees - per * (n - 1)) * 100) / 100 : per,
      })),
    );
  };

  const handleSave = (): void => {
    const body: SaveSupplierPaymentPlanRequest = {
      terms: filledRows.map((r) => ({
        amountPaise: rupeesToPaise(r.amount as number),
        ...(r.dueDate ? { dueDate: toIsoDate(r.dueDate) as string } : {}),
        ...(r.note.trim() ? { note: r.note.trim() } : {}),
      })),
    };
    save.mutate({ invoiceSupplierId: line.id, body }, { onSuccess: () => onOpenChange(false) });
  };

  return (
    <UzDialog
      open={open}
      onOpenChange={onOpenChange}
      title="Plan supplier installments"
      description={
        line.supplierName
          ? `Schedule how ${line.supplierName}'s payable of ${formatInrPaise(line.payablePaise)} will be paid.`
          : 'Schedule how this payable will be paid, in terms.'
      }
      footer={
        <div className="flex items-center justify-between gap-2">
          <span
            className={
              Math.abs(diff) < 0.005
                ? 'text-xs font-medium text-emerald-600'
                : 'text-xs font-medium text-amber-600'
            }
          >
            Planned {formatInrPaise(rupeesToPaise(plannedRupees))} of{' '}
            {formatInrPaise(line.payablePaise)}
            {Math.abs(diff) >= 0.005 &&
              ` (${diff > 0 ? '+' : ''}${formatInrPaise(rupeesToPaise(diff))})`}
          </span>
          <div className="flex gap-2">
            <UzButton variant="outline" onClick={() => onOpenChange(false)}>
              Cancel
            </UzButton>
            <UzButton onClick={handleSave} loading={save.isPending}>
              Save plan
            </UzButton>
          </div>
        </div>
      }
    >
      <div className="space-y-2">
        {rows.map((row, i) => (
          <div key={i} className="flex items-end gap-2">
            <UzMoneyInput
              label={i === 0 ? 'Amount' : undefined}
              value={row.amount}
              onValueChange={(v) => setRow(i, { amount: v ?? '' })}
              containerClassName="flex-1"
            />
            <UzDatePicker
              label={i === 0 ? 'Due date' : undefined}
              value={row.dueDate}
              onValueChange={(d) => setRow(i, { dueDate: d })}
              containerClassName="flex-1"
            />
            <UzInput
              label={i === 0 ? 'Note' : undefined}
              value={row.note}
              onChange={(e) => setRow(i, { note: e.target.value })}
              containerClassName="flex-1"
            />
            <UzButton
              variant="ghost"
              size="sm"
              className="mb-1"
              aria-label="Remove term"
              onClick={() => removeRow(i)}
            >
              <Trash2 className="h-4 w-4 text-zinc-400" />
            </UzButton>
          </div>
        ))}
        <div className="flex gap-2 pt-1">
          <UzButton
            variant="outline"
            size="sm"
            leftIcon={<PlusCircle className="h-4 w-4" />}
            onClick={addRow}
          >
            Add term
          </UzButton>
          <UzButton variant="ghost" size="sm" onClick={splitEvenly}>
            Split payable evenly
          </UzButton>
        </div>
      </div>
    </UzDialog>
  );
};

/** Thin wrapper so the dialog chrome stays out of the field-heavy body above. */
const UzDialogWrapper = ({
  open,
  onOpenChange,
  supplierName,
  canSubmit,
  pending,
  onSubmit,
  isEdit,
  children,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  supplierName: string | null;
  canSubmit: boolean;
  pending: boolean;
  onSubmit: () => void;
  isEdit: boolean;
  children: React.ReactNode;
}): React.JSX.Element => (
  <UzDialog
    open={open}
    onOpenChange={onOpenChange}
    title={isEdit ? 'Edit supplier payment' : 'Record supplier payment'}
    description={
      isEdit
        ? 'Void the original payment and re-record it with the corrected details.'
        : supplierName
          ? `Log a payment made to ${supplierName}.`
          : 'Log a payment against this line.'
    }
    footer={
      <div className="flex justify-end gap-2">
        <UzButton variant="outline" onClick={() => onOpenChange(false)}>
          Cancel
        </UzButton>
        <UzButton onClick={onSubmit} disabled={!canSubmit} loading={pending}>
          {isEdit ? 'Save changes' : 'Record payment'}
        </UzButton>
      </div>
    }
  >
    {children}
  </UzDialog>
);
